/**
 * Calculation Utilities for Invoice
 * Contains calculation logic for subtotals, totals, VAT, discounts, etc.
 */

import { timeToDecimalHours } from './timeConversion.utils';

/**
 * Calculate subtotal based on time and price
 * Logic from InvoiceTool.js:
 * - For Hourly pricing: Convert time (HH:MM) to decimal hours and multiply by hourly rate
 * - For Daily/Weekly/Monthly/Agreement: Multiply units by rate
 * 
 * @param timeStr - Time string in HH:MM format or units as string
 * @param hourlyRate - Rate per hour/unit
 * @param discount - Discount object with value and type
 * @param isHourlyPricing - Optional flag to indicate if hourly pricing (default: auto-detect)
 * @returns Calculated subtotal after discount
 */
export const calculateSubtotal = (
  timeStr: string,
  hourlyRate: number,
  discount: { value: number; type: 'percentage' | 'fixed' },
  isHourlyPricing?: boolean
): number => {
  if (!timeStr || !hourlyRate || hourlyRate <= 0) return 0;

  let baseAmount = 0;

  // Auto-detect if hourly pricing based on time format (contains ':')
  const isHourly = isHourlyPricing !== undefined ? isHourlyPricing : timeStr.includes(':');

  if (isHourly) {
    // For Hourly pricing: Convert time (HH:MM) to decimal hours
    const decimalHours = timeToDecimalHours(timeStr);
    baseAmount = decimalHours * hourlyRate;
  } else {
    // For Daily/Weekly/Monthly/Agreement: timeStr is units (number)
    const units = parseFloat(timeStr) || 0;
    baseAmount = units * hourlyRate;
  }
  
  // Apply discount
  let discountAmount = 0;
  if (discount && discount.value > 0) {
    if (discount.type === 'percentage') {
      discountAmount = (baseAmount * discount.value) / 100;
    } else if (discount.type === 'fixed') {
      discountAmount = discount.value;
    }
  }
  
  // Return subtotal after discount (ensure non-negative)
  return Math.max(0, baseAmount - discountAmount);
};

/**
 * Calculate total with VAT
 * @param subtotal - Subtotal amount before VAT
 * @param vatRate - VAT rate as percentage (default 25%)
 * @returns Total amount including VAT
 */
export const calculateTotalWithVAT = (subtotal: number, vatRate: number = 25): number => {
  return subtotal * (1 + vatRate / 100);
};

/**
 * Calculate VAT amount only
 * @param subtotal - Subtotal amount before VAT
 * @param vatRate - VAT rate as percentage
 * @returns VAT amount
 */
export const calculateVATAmount = (subtotal: number, vatRate: number = 25): number => {
  return (subtotal * vatRate) / 100;
};

/**
 * Calculate discount amount
 * @param amount - Original amount
 * @param discount - Discount object
 * @returns Discount amount
 */
export const calculateDiscountAmount = (
  amount: number,
  discount: { value: number; type: 'percentage' | 'fixed' }
): number => {
  if (discount.type === 'percentage') {
    return (amount * discount.value) / 100;
  }
  return discount.value;
};

/**
 * Calculate pension amount (typically 4.5% of gross salary)
 * @param grossSalary - Total gross salary
 * @param pensionPercentage - Pension percentage (default 4.5%)
 * @returns Pension amount
 */
export const calculatePensionAmount = (
  grossSalary: number,
  pensionPercentage: number = 4.5
): number => {
  return (grossSalary * pensionPercentage) / 100;
};

/**
 * Calculate holiday savings amount (typically 12% of gross salary)
 * @param grossSalary - Total gross salary
 * @param holidayPercentage - Holiday savings percentage (default 12%)
 * @returns Holiday savings amount
 */
export const calculateHolidaySavingsAmount = (
  grossSalary: number,
  holidayPercentage: number = 12
): number => {
  return (grossSalary * holidayPercentage) / 100;
};

/**
 * Calculate net amount after deductions
 * @param grossAmount - Gross amount
 * @param deductions - Array of deduction amounts
 * @returns Net amount
 */
export const calculateNetAmount = (grossAmount: number, deductions: number[]): number => {
  const totalDeductions = deductions.reduce((sum, deduction) => sum + deduction, 0);
  return Math.max(0, grossAmount - totalDeductions);
};

/**
 * Calculate ROT/RUT deduction amount
 * ROT: 30% of labor cost (max 50,000 SEK per person per year)
 * RUT: 50% of labor cost (max 75,000 SEK per person per year)
 * 
 * @param laborCost - Labor cost amount
 * @param deductionType - Type of deduction ('rot' | 'rut')
 * @returns Deduction amount
 */
export const calculateRotRutDeduction = (
  laborCost: number,
  deductionType: 'rot' | 'rut'
): number => {
  if (deductionType === 'rot') {
    const rotDeduction = laborCost * 0.30; // 30%
    return Math.min(rotDeduction, 50000); // Max 50,000 SEK
  } else if (deductionType === 'rut') {
    const rutDeduction = laborCost * 0.50; // 50%
    return Math.min(rutDeduction, 75000); // Max 75,000 SEK
  }
  return 0;
};

/**
 * Calculate express payment fee (typically 1.95% of invoice amount)
 * @param invoiceAmount - Total invoice amount
 * @param feePercentage - Express fee percentage (default 1.95%)
 * @returns Express payment fee
 */
export const calculateExpressFee = (
  invoiceAmount: number,
  feePercentage: number = 1.95
): number => {
  return (invoiceAmount * feePercentage) / 100;
};

/**
 * Round amount to specified decimal places
 * @param amount - Amount to round
 * @param decimals - Number of decimal places (default 2)
 * @returns Rounded amount
 */
export const roundAmount = (amount: number, decimals: number = 2): number => {
  return Math.round(amount * Math.pow(10, decimals)) / Math.pow(10, decimals);
};

/**
 * Format amount as currency string
 * @param amount - Amount to format
 * @param currency - Currency code (default 'SEK')
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number, currency: string = 'SEK'): string => {
  return `${roundAmount(amount, 2).toFixed(2)} ${currency}`;
};

/**
 * Calculate total working hours from multiple time entries
 * @param timeEntries - Array of time strings in HH:MM format
 * @returns Total hours in decimal format
 */
export const calculateTotalWorkingHours = (timeEntries: string[]): number => {
  return timeEntries.reduce((total, timeStr) => {
    return total + timeToDecimalHours(timeStr);
  }, 0);
};

/**
 * Calculate average hourly rate from total amount and hours
 * @param totalAmount - Total amount earned
 * @param totalHours - Total hours worked
 * @returns Average hourly rate
 */
export const calculateAverageHourlyRate = (totalAmount: number, totalHours: number): number => {
  if (totalHours === 0) return 0;
  return totalAmount / totalHours;
};
